import { useCallback, useMemo, useState } from 'react';
import moment from 'moment-timezone';

import { Filter } from '../../../../components/timePeriodAndFilterPicker/useFilterPicker';
import { getUserTimeZone } from '../../../../lib/timeZone';
import { IDevicePerformanceEventsParams } from '../../../../services/core/performance';

/** Real UTC instants, as epoch milliseconds */
export type ProcessCrashesRange = {
  from: number,
  to: number
};

/**
 * Everything the Crashes tab's chart and its export need in order to describe the same data. It is captured
 * whenever the time period or device filter changes, rather than recalculated for each request, so the range can't
 * drift between the chart, its export and the days which can be selected from it.
 */
export interface ProcessCrashesScope extends ProcessCrashesRange {
  days: number,
  timeZone: string,
  filter: Filter
}

const dayFormat = 'YYYY-MM-DD';

/** Today plus the preceding days - 1 local calendar days, up to the end of the current hour, in the given IANA time zone */
export const getProcessCrashesRange = (days: number, timeZone: string, now: number): ProcessCrashesRange => {
  const localNow = moment.utc(now).tz(timeZone);
  return {
    from: localNow.clone().startOf('day').subtract(days - 1, 'days').valueOf(),
    to: localNow.clone().endOf('hour').valueOf()
  };
};

/**
 * The range as the daily events API identifies days: each local calendar day is represented by that date at UTC
 * midnight, rather than by the real instant at which that day starts. It positions the chart's columns, so is what
 * the chart's date axis has to be drawn from.
 */
export const getDailyKeyRange = ({ from, to }: ProcessCrashesRange, timeZone: string): ProcessCrashesRange => ({
  from: moment.utc(moment.utc(from).tz(timeZone).format(dayFormat)).valueOf(),
  to: moment.utc(moment.utc(to).tz(timeZone).format(`${dayFormat} HH`), `${dayFormat} HH`).endOf('hour').valueOf()
});

/**
 * The real range of a local calendar day ('YYYY-MM-DD'), within the range being displayed. A local day is 23, 24
 * or 25 real hours long, so its end can't be found by adding a fixed duration to its start.
 */
export const getDayRange = (day: string, range: ProcessCrashesRange, timeZone: string): ProcessCrashesRange => {
  const start = moment.tz(day, dayFormat, timeZone);
  return {
    from: Math.max(start.valueOf(), range.from),
    to: Math.min(start.clone().endOf('day').valueOf(), range.to)
  };
};

/** The chart and export request for a scope: process crash events, over exactly the scope's range */
export const getProcessCrashesParams = (scope: ProcessCrashesScope): IDevicePerformanceEventsParams => ({
  name: 'processCrashed',
  days: scope.days,
  from: scope.from,
  to: scope.to,
  timeZone: scope.timeZone,
  homeLocationId: scope.filter.homeLocation?.value.id,
  group: scope.filter.group?.value,
  zoneId: scope.filter.zone?.value.id,
  model: scope.filter.model?.value
});

/**
 * The range being charted, and which (if any) local calendar day is selected from it. Selecting the selected day
 * again clears it, and any change of time period or device filter - a new scope - clears the selection too.
 */
export function useProcessCrashesScope(params: { days: number, filter: Filter, timeZone?: string }) {
  const { days, filter, timeZone = getUserTimeZone() } = params;

  const scope: ProcessCrashesScope = useMemo(() => ({
    days,
    timeZone,
    filter,
    ...getProcessCrashesRange(days, timeZone, moment.utc().valueOf())
  }), [days, timeZone, filter]);

  // The selection records the scope it was made in, so a new scope never inherits it.
  const [selection, setSelection] = useState<{ scope: ProcessCrashesScope, day: string }>();
  const selectedDay = selection?.scope === scope ? selection.day : undefined;

  const toggleDay = useCallback((day: string) => {
    setSelection(current => (current?.scope === scope && current.day === day) ? undefined : { scope, day });
  }, [scope]);

  const selectedDayRange = useMemo(() => selectedDay ? getDayRange(selectedDay, scope, timeZone) : undefined, [selectedDay, scope, timeZone]);

  return { scope, selectedDay, selectedDayRange, toggleDay };
}
